<template>
    <div class="forum-updates widget panel">

        <div class="panel-heading">
            <span
                class="badge"
                v-if="showBadge">
                {{ badgeStr }}
            </span>
            <h2>
                Forum Replies
                <i
                    class="clippie fa fa-info-circle"
                    v-tooltip="'Replies to forum threads you are apart of.'">
                </i>
            </h2>
            <div
                class="toggle-widget"
                @click="open = !open">

                <i class="fa fa-minus" v-if="open"></i>
                <i class="fa fa-plus" v-else></i>
            </div>
        </div>

        <div
            class="panel-body"
            v-show="open">

            <scroll-area
                ref="scrollbar"
                :wheelPropagationEnabled="false">

                <ul v-if="!isFetching && !error && notifications.length > 0">
                    <li v-for="notif in notifications">

                        <div class="post-thread">
                            <router-link
                                :title="notif.thread.title"
                                :to="getThreadUrl(notif.thread)">
                                {{ notif.thread.title }}
                            </router-link>
                        </div>

                        <div class="post-date">
                            {{ notif.post.createdAt | shortDate }}
                        </div>

                        <div class="author">
                            <ForumAvatar
                                :src="notif.post.author.avatarUrl">
                            </ForumAvatar>
                            <router-link
                                class="author-name"
                                :to="getAuthorUrl(notif.post.author)">
                                {{ notif.post.author.name }}
                            </router-link>
                        </div>

                        <div class="post-content">
                            <router-link
                                :to="getPostUrl(notif.thread, notif.post)">
                                {{ notif.post.content | stripTags | truncate(50) }}
                            </router-link>
                        </div>
                    </li>
                </ul>

                <p
                    class="empty-msg"
                    v-if="!isFetching && !error && notifications.length === 0">
                    No replies to your threads or posts.
                </p>

                <p
                    class="empty-msg"
                    v-if="!isFetching && error">
                    Failed to fetch replies.
                </p>

                <Loader v-if="isFetching">
                </Loader>

            </scroll-area>
        </div>

    </div>
</template>

<script>
    // ForumUpdates: the sidebar widget that displays recent forum activity of
    // interest to the current user. Currently, "activity of interest" includes
    // any replies to their threads or posts.

    import ForumService from '../../services/forum';
    import ForumUtils   from '../../utils/forum';
    import ForumAvatar  from '../reusable/forum/avatar';
    import Loader       from '../reusable/forum/loader';
    import ScrollArea   from '../reusable/scroll-area';

    export default {
        components: {
            ForumAvatar,
            Loader,
            ScrollArea,
        },

        data() {
            return {
                open: false,
                notifications: [],
                queuedMessages: [],
                isFetching: false,
                error: null,
            };
        },

        computed: {
            userId() {
                return parseInt(localStorage.user_id);
            },

            showBadge() {
                return !this.isFetching && this.notifications.length > 0;
            },

            badgeStr() {
                return this.notifications.length.toString();
            },
        },

        watch: {
            open() {
                this.scheduleScrollbarUpdate();
            },

            notifications() {
                this.scheduleScrollbarUpdate();
            },

            isFetching() {
                this.scheduleScrollbarUpdate();
            },
        },

        async created() {
            this.initSockets();
            await this.fetchNotifications();
        },

        methods: {
            initSockets() {

                const types = [
                    'forumNotificationCreated',
                    'forumNotificationUpdated',
                    'forumNotificationDeleted',
                ];
                for (const type of types) {
                    this.sockets.subscribe(type, ({ message }) => {
                        this.processMessage(type, message);
                    });
                }
            },

            processMessage(type, message) {
                function parseDate(date) {
                    if (typeof date === 'string') {
                        return new Date(date);
                    } else {
                        return date;
                    }
                }
                function parseTimestamps(obj) {
                    if (obj.createdAt) {
                        obj.createdAt = parseDate(obj.createdAt);
                    }
                    if (obj.updatedAt) {
                        obj.updatedAt = parseDate(obj.updatedAt);
                    }
                    if (obj.deletedAt) {
                        obj.deletedAt = parseDate(obj.deletedAt);
                    }
                    if (obj.readAt) {
                        obj.readAt = parseDate(obj.readAt);
                    }
                }
                parseTimestamps(message);
                if (message.thread) {
                    parseTimestamps(message.thread);
                }
                if (message.post) {
                    parseTimestamps(message.post);
                }
                if (message.sourcePost) {
                    parseTimestamps(message.sourcePost);
                }

                if (this.isFetching) {
                    this.queuedMessages.push({ type, message });
                    return;
                }

                switch (type) {
                case 'forumNotificationCreated':
                    const exists = this.notifications.find(notif => {
                        return notif.id === message.id;
                    });
                    if (!exists) {
                        this.notifications.push(message);
                    }
                    break;

                case 'forumNotificationUpdated':
                    this.notifications = this.notifications.map(notif => {
                        if (notif.id === message.id) {
                            return message;
                        } else {
                            return notif;
                        }
                    }).filter(n => !n.readAt);
                    break;

                case 'forumNotificationDeleted':
                    this.notifications = this.notifications.filter(notif => {
                        return notif.id !== message.id;
                    });
                    break;
                }
            },

            async fetchNotifications() {

                // Fetch notifications
                try {
                    this.isFetching = true;
                    this.error = null;

                    const notifs = await ForumService.getNotificationsForUser(
                        this.userId,
                        { read: false },
                    );
                    this.notifications = notifs;

                } catch (error) {
                    this.error = error;
                    console.error('failed to fetch forum notifications:', error);

                } finally {
                    this.isFetching = false;
                }

                // Process queued messages
                for (const { type, message } of this.queuedMessages) {
                    this.processMessage(type, message);
                }
                this.queuedMessages.length = 0;
            },

            scheduleScrollbarUpdate() {
                this.$nextTick(() => {
                    if (this.$refs.scrollbar) {
                        this.$refs.scrollbar.update();
                    }
                });
            },

            getAuthorUrl(author) {
                if (author && author.id) {
                    return {
                        name: 'forum-user',
                        params: {
                            userId: author.id,
                        },
                    };
                } else {
                    return '';
                }
            },

            getThreadUrl(thread) {
                const slug = ForumUtils.formatTitlePreview(thread.title);
                return {
                    name: 'forum-thread',
                    params: {
                        threadId: thread.id,
                        threadTitlePreview: slug,
                    },
                };
            },

            getPostUrl(thread, post) {
                const slug = ForumUtils.formatTitlePreview(thread.title);
                return {
                    name: 'forum-post',
                    params: {
                        threadId: thread.id,
                        threadTitlePreview: slug,
                        postId: post.id,
                    },
                };
            },
        }
    }
</script>

<style lang="scss" scoped>
    .scroll-area {
        margin: auto;
        max-height: 250px;
    }

    ul {
        margin-right: 10px;
    }

    li {
        border-bottom: 1px solid #333;
        padding: 10px;

        &:last-of-type {
            border-bottom: none;
        }
    }

    .post-thread {
        display: flex;

        a {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .post-date {
        margin-top: 2px;
    }

    .author {
        display: flex;
        max-width: 100%;
        margin-top: 15px;

        .forum-avatar {
            width: 20px;
            height: 20px;
            font-size: 18px;
            margin-right: 5px;
        }

        .author-name {
            color: inherit;
            font-weight: bold;

            &:hover {
                color: #33ccff;
                text-decoration: none;
            }
        }
    }

    .post-content {
        margin-top: 2px;
        display: flex;

        a {
            color: inherit;
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &:hover {
                color: #33ccff;
            }
        }
    }

    .forum-loader {
        border-top: 1px solid #333;
    }

    .empty-msg {
        text-align: center;
        margin: 20px 10px;
    }
</style>



// WEBPACK FOOTER //
// ForumUpdates.vue?59b0249b